/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { Fragment, useState } from 'react'

import {
  EuiButtonEmpty,
  EuiCode,
  EuiEmptyPrompt,
  EuiModal,
  EuiModalBody,
  EuiOverlayMask,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useEuiFontSize,
  useEuiTheme,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { DeploymentTemplateInfoV2 } from '@modules/cloud-api/v1/types'
import type { GettingStartedType, StackDeployment } from '@modules/ui-types'

import StackElasticCredentials from '../../../StackElasticCredentials'
import { messages } from '../messages'
import saveCredentialsModalHeaderDark from '../../../../files/save-credentials-darkmode.svg'
import saveCredentialsModalHeaderLight from '../../../../files/save-credentials-lightmode.svg'

type Props = {
  deployment: StackDeployment
  deploymentTemplate?: DeploymentTemplateInfoV2
  instanceType?: GettingStartedType
  onDismiss?: () => void
}

function SaveDeploymentCredentialsModalBase({
  deployment,
  deploymentTemplate,
  instanceType,
  onDismiss,
}: Props) {
  const { euiTheme, colorMode } = useEuiTheme()
  const [isOpen, setIsOpen] = useState(true)
  const { fontSize: fontSizeM } = useEuiFontSize('l', { unit: 'px' })

  const closeModal = (): void => {
    setIsOpen(false)

    if (onDismiss) {
      onDismiss()
    }
  }

  const getTitle = () => {
    const title = {
      ...messages.saveCredentialsModalTitle,
      values: {
        elastic: <EuiCode>elastic</EuiCode>,
      },
    }
    return (
      <EuiTitle size='s'>
        <h3 className='save-credentials-modal-title' data-test-id='save-credentials-modal-title'>
          <FormattedMessage {...title} />
        </h3>
      </EuiTitle>
    )
  }

  const getInstanceInfo = () => {
    if (instanceType !== 'kibana') {
      return null
    }

    return (
      <Fragment>
        <EuiText color='subdued' data-test-id='save-credentials-modal-instance-info'>
          <FormattedMessage {...messages.saveCredentialsModalInfo} />
        </EuiText>

        <EuiSpacer size='m' />
      </Fragment>
    )
  }

  if (!isOpen) {
    return null
  }

  const saveCredentialsModalStyles = css`
    min-height: 630px;

    .euiModal__closeIcon {
      display: none;
    }
  `

  const saveCredentialsModalPromptStyles = css`
    padding-top: ${euiTheme.size.xs};
    padding-bottom: 0;

    .save-credentials-modal-title {
      font-size: ${fontSizeM};
      font-weight: 400;
      line-height: 2rem;
    }

    img {
      height: 190px;
      width: 190px;
    }

    .euiPanel {
      padding: 1px;
      background-color: ${euiTheme.colors.body};
      box-shadow: none;

      .theme-dark & {
        background-color: ${euiTheme.colors.darkestShade};
      }
    }
  `

  return (
    <EuiOverlayMask>
      <EuiModal
        data-test-id='save-deployment-credential-modal'
        onClose={closeModal}
        css={saveCredentialsModalStyles}
        maxWidth={540}
      >
        <EuiModalBody>
          <EuiEmptyPrompt
            css={saveCredentialsModalPromptStyles}
            iconType={
              colorMode === 'DARK'
                ? saveCredentialsModalHeaderDark
                : saveCredentialsModalHeaderLight
            }
            title={getTitle()}
            body={
              <Fragment>
                {getInstanceInfo()}

                <EuiPanel hasShadow={false} paddingSize='none'>
                  <StackElasticCredentials
                    onlyShowCredentials={true}
                    deploymentTemplate={deploymentTemplate}
                    deployment={deployment}
                    onDownloadCredentials={closeModal}
                  />
                </EuiPanel>

                <EuiSpacer size='m' />

                <EuiButtonEmpty onClick={closeModal} data-test-id='continue-without-download'>
                  <EuiText size='s'>
                    <FormattedMessage {...messages.continueWithoutDownloading} />
                  </EuiText>
                </EuiButtonEmpty>
              </Fragment>
            }
          />
        </EuiModalBody>
      </EuiModal>
    </EuiOverlayMask>
  )
}

export default SaveDeploymentCredentialsModalBase
